import React, { useState, useEffect } from 'react';
import { fetchPayments, deletePayment, fetchProfile as fetchProfileAPI } from '../utils/api';
import { formatDate } from '../utils/date';
import { ADMIN_ROLE, FINANCIAL_STAFF } from '../utils/roles';
import './Expenses.css';
import './Invoices.css';
import './_Pagination.css';
import './_Buttons.css';
import './_Input.css';

const Payments = () => {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sortBy, setSortBy] = useState('date');
    const [sortDir, setSortDir] = useState('desc');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [total, setTotal] = useState(0);
    const [amount, setAmount] = useState(0);
    const [profile, setProfile] = useState(null);

    const initialFromDate = new Date();
    initialFromDate.setMonth(initialFromDate.getMonth() - 1);
    const fromDateString = initialFromDate.toISOString().split('T')[0];
    const [from, setFrom] = useState(fromDateString);
    const [to, setTo] = useState('');

    const getPayments = async () => {
        try {
            setLoading(true);
            const response = await fetchPayments(sortBy, sortDir, page, limit, from, to);
            setPayments(response.data.payments || []);
            setTotal(response.data.total || 0);
            setAmount(response.data.amount || 0);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const loadData = async () => {
            try {
                const profileData = await fetchProfileAPI();
                setProfile(profileData.data);
                getPayments();
            } catch (error) {
                setError(error.message);
            }
        };

        loadData();
    }, [sortBy, sortDir, page, limit, from, to]);

    const handleSort = (newSortBy) => {
        setSortDir(sortBy === newSortBy && sortDir === 'asc' ? 'desc' : 'asc');
        setSortBy(newSortBy);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this payment?')) {
            try {
                await deletePayment(id);
                getPayments();
            } catch (error) {
                setError(error.message);
            }
        }
    };

    const handleLimitChange = (e) => {
        setLimit(parseInt(e.target.value, 10));
        setPage(1);
    };

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= Math.ceil(total / limit)) {
            setPage(newPage);
        }
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    const totalPages = Math.ceil(total / limit) || 1;

    const canManage = profile && profile.roles.some(role => role.id === ADMIN_ROLE || role.id === FINANCIAL_STAFF);

    return (
        <div className="expenses-container">
            <div className="header">
                <h1>Payments | {total}</h1>
                <div className="header-controls">
                    <div className="showing-control">
                        <label htmlFor="limit">Showing:</label>
                        <select id="limit" value={limit} onChange={handleLimitChange}>
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                </div>
            </div>
            <div className="expenses-filters">
                <div className='expenses-labels'>
                    <label>From: <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
                    <label>To: <input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
                </div>
                <div className="invoices-total-amount">
                    ${(amount / 100).toFixed(2)}
                </div>
            </div>
            {error && <p className="error">{error}</p>}
            <table className="expenses-table">
                <thead>
                    <tr>
                        <th onClick={() => handleSort('date')}>
                            Date {sortBy === 'date' && (sortDir === 'asc' ? '▲' : '▼')}
                        </th>
                        <th onClick={() => handleSort('amount')}>
                            Amount {sortBy === 'amount' && (sortDir === 'asc' ? '▲' : '▼')}
                        </th>
                        <th>Invoice</th>
                        <th>Student</th>
                        {canManage && <th>Actions</th>}
                    </tr>
                </thead>
                <tbody>
                    {payments.map((payment) => (
                        <tr key={payment.id}>
                            <td>{formatDate(payment.date)}</td>
                            <td>${(payment.amount / 100).toFixed(2)}</td>
                            <td>{payment.invoice.name}</td>
                            <td>{`${payment.student.first_name} ${payment.student.last_name}`}</td>
                            {canManage && <td><button onClick={() => handleDelete(payment.id)} className="submit-button text-button">Delete</button></td>}
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan={canManage ? 5 : 4}>
                            <div className="pagination-controls">
                                <div className="page-buttons">
                                    <button onClick={() => handlePageChange(page - 1)} disabled={page === 1}>
                                        {'<'}
                                    </button>
                                    <span>Page {page} of {totalPages}</span>
                                    <button onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>
                                        {'>'}
                                    </button>
                                </div>
                            </div>
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};

export default Payments;
